(function() {
  const ROOT = "[data-fsa-addon]";
  const ELEMENT_NAME = "front-shock-assembly-addon";
  const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };
  const toText = (value) => typeof value === "string" ? value.trim() : "";
  const asArray = (value) => {
    if (Array.isArray(value)) return value.map(toText).filter(Boolean);
    const text = toText(value);
    return text ? [text] : [];
  };
  const isInteractiveElement = (target) => {
    if (!(target instanceof Element)) return false;
    return Boolean(
      target.closest(
        [
          "[data-fsa-toggle]",
          "[data-fsa-details]",
          "[data-popup-target]",
          "a",
          "button",
          "input",
          "select",
          "textarea",
          "label",
          "summary"
        ].join(", ")
      )
    );
  };
  const money = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(cents, window.shopCurrencyFormat || "${{amount}}");
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(toNumber(cents) / 100);
  };
  const readConfig = (widget) => {
    const dataEl = widget.querySelector("[data-fsa-addon-data]");
    if (!dataEl) return null;
    try {
      return JSON.parse(dataEl.textContent);
    } catch (error) {
      console.warn("[FSA addon] Failed to parse JSON config:", error);
      return null;
    }
  };
  class FrontShockAssemblyAddon {
    constructor(widget) {
      this.widget = widget;
      this.controller = new AbortController();
      this.config = readConfig(widget);
      this.mode = toText(widget.dataset.fsaMode) || "addon";
      this.defaultSelected = String(widget.dataset.fsaDefaultSelected) === "true";
      this.redirectUrl = toText(widget.dataset.fsaRedirectUrl);
      this.selected = false;
      this.activeEntry = null;
      this.selectionInitialized = false;
      this.refreshRaf = 0;
      this.bind();
      this.refresh();
    }
    bind() {
      const toggle = this.widget.querySelector("[data-fsa-toggle]");
      if (toggle) {
        toggle.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (this.mode === "redirect" && this.selected && this.redirectUrl) {
            window.location.assign(this.redirectUrl);
            return;
          }
          this.setSelected(!this.selected);
        }, { signal: this.controller.signal });
      }
      const card = this.widget.querySelector("[data-fsa-card]");
      if (card) {
        card.addEventListener("click", (event) => {
          if (isInteractiveElement(event.target)) return;
          this.toggleFromCard();
        }, { signal: this.controller.signal });
        card.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          if (isInteractiveElement(event.target)) return;
          event.preventDefault();
          this.toggleFromCard();
        }, { signal: this.controller.signal });
      }
      this.widget.querySelectorAll("[data-fsa-choice]").forEach((button) => {
        button.addEventListener("click", () => {
          this.setSelected(button.dataset.fsaChoice === "yes");
        }, { signal: this.controller.signal });
      });
      document.addEventListener("variant:change", () => this.scheduleRefresh(), {
        signal: this.controller.signal
      });
      document.addEventListener("change", (event) => {
        if (event.target && event.target.closest && event.target.closest("variant-form-custom")) {
          window.setTimeout(() => this.scheduleRefresh(), 80);
        }
      }, { signal: this.controller.signal });
    }
    destroy() {
      this.controller.abort();
      if (this.refreshRaf) window.cancelAnimationFrame(this.refreshRaf);
    }
    applyPdpState(state) {
      var _a;
      if (state == null ? void 0 : state.addonConfig) this.config = state.addonConfig;
      const preserveSelection = Boolean((_a = state == null ? void 0 : state.addonSelections) == null ? void 0 : _a.fsa);
      this.selected = false;
      this.activeEntry = null;
      this.selectionInitialized = false;
      this.refresh();
      if (preserveSelection) this.setSelected(true, false);
    }
    scheduleRefresh() {
      if (this.refreshRaf) return;
      this.refreshRaf = window.requestAnimationFrame(() => {
        this.refreshRaf = 0;
        this.refresh();
      });
    }
    findMatchingEntry() {
      const entries = Array.isArray(this.config && this.config.entries) ? this.config.entries : [];
      if (!entries.length) return null;
      const product = this.config && this.config.product || {};
      const platformKey = toText(product.platformKey);
      const shockKey = toText(product.shockKey);
      if (!platformKey || !shockKey) return null;
      return entries.find((entry) => {
        const entryPlatforms = asArray(entry.platform);
        const entryShocks = asArray(entry.shock);
        return entryPlatforms.includes(platformKey) && entryShocks.includes(shockKey);
      }) || null;
    }
    refresh() {
      const isRedirectMode = this.mode === "redirect";
      if (isRedirectMode) {
        this.activeEntry = null;
        this.widget.classList.remove("hidden");
      } else {
        this.activeEntry = this.findMatchingEntry();
        const hasEntry = Boolean(this.activeEntry);
        this.widget.classList.toggle("hidden", !hasEntry);
        if (!hasEntry) {
          this.selectionInitialized = false;
          this.setSelected(false, false);
          return;
        }
      }
      const priceTarget = this.widget.querySelector("[data-fsa-price]");
      if (priceTarget && this.mode !== "redirect") {
        priceTarget.textContent = `(+${money(this.getSelectedTotalCents())})`;
      }
      const cardPriceTarget = this.widget.querySelector("[data-fsa-card-price]");
      if (cardPriceTarget && this.mode !== "redirect") {
        cardPriceTarget.textContent = money(this.getSelectedTotalCents());
      }
      if (!this.selectionInitialized) {
        this.selectionInitialized = true;
        this.setSelected(this.defaultSelected, false);
        return;
      }
      this.syncPresentation();
    }
    syncPresentation() {
      this.widget.dataset.selected = this.selected ? "true" : "false";
      const card = this.widget.querySelector("[data-fsa-card]");
      if (card) {
        card.dataset.selected = this.selected ? "true" : "false";
        card.setAttribute("aria-pressed", this.selected ? "true" : "false");
      }
      const toggle = this.widget.querySelector("[data-fsa-toggle]");
      if (toggle) {
        toggle.dataset.selected = this.selected ? "true" : "false";
        toggle.setAttribute("aria-pressed", this.selected ? "true" : "false");
      }
      const note = this.widget.querySelector("[data-fsa-top-hat-note]");
      if (note) note.classList.toggle("hidden", !this.selected);
    }
    toggleFromCard() {
      if (this.mode === "redirect" && this.selected && this.redirectUrl) {
        window.location.assign(this.redirectUrl);
        return;
      }
      this.setSelected(!this.selected);
    }
    setSelected(isSelected, emit = true) {
      const canSelect = this.mode === "redirect" || Boolean(this.activeEntry);
      this.selected = Boolean(isSelected && canSelect);
      this.widget.querySelectorAll("[data-fsa-choice]").forEach((button) => {
        const isActive = button.dataset.fsaChoice === "yes" === this.selected;
        button.classList.toggle("is-selected", isActive);
        button.dataset.selected = isActive ? "true" : "false";
        button.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
      this.syncPresentation();
      if (emit) {
        document.dispatchEvent(new CustomEvent("front-shock-assembly:addon-change", {
          detail: {
            selected: this.selected,
            totalCents: this.getSelectedTotalCents(),
            topHatTitle: this.activeEntry ? this.activeEntry.topHatTitle : ""
          }
        }));
        if (window.MudifyAddonPriceSync && typeof window.MudifyAddonPriceSync.applyCombinedPrice === "function") {
          window.MudifyAddonPriceSync.applyCombinedPrice();
        }
      }
    }
    getSelectedTotalCents() {
      if (this.mode === "redirect" || !this.activeEntry) return 0;
      return toNumber(this.activeEntry.bundlePrice);
    }
    getSelectedItems(quantity = 1) {
      if (this.mode === "redirect" || !this.selected || !this.activeEntry) return [];
      const qty = Math.max(1, toNumber(quantity) || 1);
      const groupId = `fsa-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const topHatTitle = toText(this.activeEntry.topHatTitle) || "Top Hat Kit";
      const shock = asArray(this.activeEntry.shock).join(", ");
      const platform = asArray(this.activeEntry.platform).join(", ");
      const bundleVariantId = toNumber(this.activeEntry.bundleVariantId);
      return [
        {
          id: bundleVariantId,
          quantity: qty,
          properties: {
            "_FSA Group": groupId,
            "_FSA Bundle": "true",
            "_FSA Top Hat Variant": String(this.activeEntry.topHatVariantId || ""),
            "_FSA Top Hat Title": topHatTitle,
            "_Addon Type": "Front Shocks Assembly",
            "_Assembly Components": `Front Shocks Assembly + ${topHatTitle}`,
            "_Assembly Shock": shock,
            "_Assembly Platform": platform
          }
        }
      ].filter((item) => toNumber(item.id) > 0);
    }
  }
  const instances = [];
  const controllers = /* @__PURE__ */ new WeakMap();
  const liveInstances = () => instances.filter((instance) => instance.widget.isConnected);
  const register = (widget) => {
    const existing = controllers.get(widget);
    if (existing) return existing;
    const controller = new FrontShockAssemblyAddon(widget);
    controllers.set(widget, controller);
    instances.push(controller);
    return controller;
  };
  const unregister = (widget) => {
    const controller = controllers.get(widget);
    if (!controller) return;
    controller.destroy();
    controllers.delete(widget);
    const index = instances.indexOf(controller);
    if (index >= 0) instances.splice(index, 1);
  };
  const initLegacyWidgets = () => {
    for (let index = instances.length - 1; index >= 0; index -= 1) {
      const instance = instances[index];
      if (instance.widget.isConnected) continue;
      unregister(instance.widget);
    }
    document.querySelectorAll(`${ROOT}:not(${ELEMENT_NAME})`).forEach(register);
  };
  class FrontShockAssemblyAddonElement extends HTMLElement {
    connectedCallback() {
      register(this);
    }
    disconnectedCallback() {
      unregister(this);
    }
  }
  if (!window.customElements.get(ELEMENT_NAME)) {
    window.customElements.define(ELEMENT_NAME, FrontShockAssemblyAddonElement);
  }
  window.MudifyFrontShockAssembly = {
    getSelectedItems(options = {}) {
      const quantity = Math.max(1, toNumber(options.quantity) || 1);
      return liveInstances().flatMap((instance) => instance.getSelectedItems(quantity));
    },
    getSelectedTotalCents() {
      return liveInstances().reduce((sum, instance) => sum + (instance.selected ? instance.getSelectedTotalCents() : 0), 0);
    },
    refresh() {
      liveInstances().forEach((instance) => instance.refresh());
    }
  };
  document.addEventListener("mudify:pdp-state:change", (event) => {
    liveInstances().forEach((instance) => {
      var _a;
      return instance.applyPdpState((_a = event.detail) == null ? void 0 : _a.state);
    });
  });
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initLegacyWidgets, { once: true });
  } else {
    initLegacyWidgets();
  }
  document.addEventListener("mudify:linked-pdp:swapped", initLegacyWidgets);
})();
